import * as React from "react";
import InputMask from "react-input-mask";

type MaskedState = {
  value: string;
  selection: { start: number; end: number } | null;
};

/**
 * `formatChars` and `beforeMaskedValueChange` are the react-input-mask v2 API
 * the app runs on, but the installed prop types describe v3 and lack both, so
 * the component is aliased with them added — the alternative is dropping the
 * per-digit range restriction and the hour cap on the time mask.
 */
const MaskedInput = InputMask as unknown as React.ComponentType<
  React.ComponentProps<typeof InputMask> & {
    formatChars?: Record<string, string>;
    beforeMaskedValueChange?: (
      newState: MaskedState,
      oldState: MaskedState,
      userInput: string | null,
    ) => MaskedState;
  }
>;

type TimeMaskProps = Omit<React.ComponentProps<typeof InputMask>, "mask" | "maskPlaceholder"> & {
  /**
   * react-input-mask types its render prop as `any` and exports no type to
   * narrow to; the child renders the actual input with the props handed to it.
   */
  children: (inputProps: any) => React.ReactNode;
};

/**
 * The per-digit mask lets the second hour digit be anything after a `2`, so a
 * keystroke or paste that would make the hour 24–29 is dropped here. Only user
 * input is checked (`userInput` is null when the value comes from props).
 */
const rejectHoursAbove23 = (
  newState: MaskedState,
  oldState: MaskedState,
  userInput: string | null,
): MaskedState => {
  const hours = newState.value.slice(0, 2);
  return userInput !== null && /^\d\d$/.test(hours) && Number(hours) > 23 ? oldState : newState;
};

/**
 * An `hh:mm` mask limited to 00:00–23:59. Wraps whatever input the caller
 * renders, so the pickup-location form and the onboarding wizard share the
 * mask without sharing a look.
 */
export const TimeMask: React.FC<TimeMaskProps> = (props) => (
  <MaskedInput
    mask="29:59"
    maskPlaceholder="hh:mm"
    formatChars={{ "2": "[0-2]", "9": "[0-9]", "5": "[0-5]" }}
    beforeMaskedValueChange={rejectHoursAbove23}
    {...props}
  />
);
